import { useQuery, useQueryClient } from '@tanstack/react-query';
import { WsEvent, type SessionView } from '@threadline/contracts';
import { api } from '@/lib/api';
import { useLive } from './use-live';

/**
 * Session list, kept warm by the socket.
 *
 * Sessions change state often (QR refresh, reconnects) and every screen shows
 * them somewhere, so this hook is the single place that reconciles the pushed
 * status events with the cached list.
 */
export function useSessions() {
  const client = useQueryClient();

  const query = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.get<SessionView[]>('/sessions'),
    staleTime: 15_000,
  });

  useLive(WsEvent.SESSION_STATUS, ({ sessionId, status, detail }) => {
    client.setQueryData<SessionView[]>(['sessions'], (previous) =>
      previous?.map((session) =>
        session.id === sessionId
          ? { ...session, status, lastError: detail ?? session.lastError }
          : session,
      ),
    );
    client.invalidateQueries({ queryKey: ['session', sessionId] });
  });

  useLive(WsEvent.SESSION_CONNECTED, ({ session }) => {
    client.setQueryData<SessionView[]>(['sessions'], (previous) =>
      previous?.map((entry) => (entry.id === session.id ? session : entry)),
    );
  });

  return query;
}

export function useSession(id: string | undefined) {
  const client = useQueryClient();

  const query = useQuery({
    queryKey: ['session', id],
    queryFn: () => api.get<SessionView>(`/sessions/${id}`),
    enabled: Boolean(id),
    // The socket pushes every change, so polling would only add load.
    staleTime: Infinity,
  });

  useLive(
    WsEvent.QR_UPDATED,
    ({ sessionId, qr }) => {
      if (sessionId !== id) return;
      client.setQueryData<SessionView>(['session', id], (previous) =>
        previous ? { ...previous, qr } : previous,
      );
    },
    Boolean(id),
  );

  useLive(
    WsEvent.SESSION_STATUS,
    ({ sessionId, status, detail }) => {
      if (sessionId !== id) return;
      client.setQueryData<SessionView>(['session', id], (previous) =>
        previous ? { ...previous, status, lastError: detail ?? previous.lastError } : previous,
      );
    },
    Boolean(id),
  );

  return query;
}
